// Cash in / out (petty cash), shown in place of the style grid: this till's
// cash movements since it opened, and Add cash / Remove cash, which open the
// same cash drawer form the SuperMarket till uses (RetailModals).
import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import db from "../../../database";
import { formatMoney } from "../../../utils/fashion/money";
import { cashBlockedReason, cashEntry, cashNet } from "../../../utils/fashion/tillTools";
import EmptyState from "./EmptyState";
import "./FashionWorkPanels.css";

// Entries are stamped "DD-MM-YYYY HH:mm:ss" (useCashManagement).
const when = (d) => {
  const m = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2})/.exec(d || "");
  const t = m ? dayjs(`${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}`) : null;
  return t?.isValid() ? t.format("DD MMM, HH:mm") : d || "";
};

export default function FashionCashPanel({ tillData, isOnline, cart, tools, addCashFlag, setAddCashFlag, setCashAddInFlag, backLabel, onClose }) {
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState("loading");

  const reload = useCallback(async () => {
    setStatus("loading");
    try {
      // toArray() then sort in memory: works the same on Dexie and SQLite.
      const rows = await db.cashInCashOut.toArray();
      setEntries([...(rows || [])].reverse());
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  }, []);

  // Load on open, and again when the cash form closes so a new entry shows.
  useEffect(() => {
    if (!addCashFlag) reload();
  }, [addCashFlag, reload]);

  const blocked = cashBlockedReason({ isOnline, cart });
  const open = (removing) => {
    if (blocked) return;
    setCashAddInFlag(removing);
    setAddCashFlag(true);
  };
  const net = cashNet(entries);

  return (
    <section className="fp-panel" aria-label="Cash in / out">
      <div className="fp-panel-head">
        <h2 className="fp-panel-title">Cash in / out</h2>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
          {backLabel}
        </button>
      </div>
      <div className="fp-row">
        {tools.addCash && (
          <button type="button" className="fp-btn-ghost" disabled={Boolean(blocked)} onClick={() => open(false)}>
            Add cash
          </button>
        )}
        {tools.removeCash && (
          <button type="button" className="fp-btn-ghost" disabled={Boolean(blocked)} onClick={() => open(true)}>
            Remove cash
          </button>
        )}
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={reload} disabled={status === "loading"}>
          Refresh
        </button>
      </div>
      {blocked && (
        <p className="fp-line-msg" role="status">
          {blocked}
        </p>
      )}
      {!tools.addCash && !tools.removeCash && <p className="fp-muted">Adding and removing cash are switched off for this till. The list shows what was moved.</p>}
      <div className="fp-wp-stats" aria-label="Cash summary">
        <div>
          <span>Entries</span>
          <b className="fp-num">{entries.length}</b>
        </div>
        <div className={net < 0 ? "is-warn" : ""}>
          <span>Net cash moved</span>
          <b className="fp-num">{formatMoney(net, tillData)}</b>
        </div>
      </div>
      {status === "error" && (
        <p className="fp-line-msg is-err" role="alert">
          Couldn't read the cash entries on this till.
        </p>
      )}
      <ul className="fp-panel-list" aria-busy={status === "loading" ? "true" : "false"}>
        {entries.length === 0 &&
          (status === "loading" ? <li className="fp-empty">Loading cash entries…</li> : <EmptyState as="li" title="No cash added or removed yet." hint="Cash added or removed at this till since it opened shows here." />)}
        {entries.map((e, i) => {
          const c = cashEntry(e);
          return (
            <li key={e.key || e.id || i} className="fp-panel-row">
              <span className="fp-panel-main">
                <span className="fp-panel-doc">
                  {c.label}
                  <span className={`fp-wp-pill is-${c.out ? "warn" : "info"}`}>{c.out ? "Out" : "In"}</span>
                </span>
                <span className="fp-muted">{[when(e.date), e.note].filter(Boolean).join(" · ")}</span>
              </span>
              <span className="fp-num">{formatMoney(c.signed, tillData)}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
